import { baseApi } from './baseApi';
import { addNotification } from '../store';
import { FolderManagerItemActionNames, FolderManagerItemActionTypes, FolderManagerItemTypes } from '../interfaces';
import { getActionsForItemType, mapSlideToFolderManagerItem } from '../helpers/folderManager.helpers';
import { isAbortError } from '../helpers/apiErrors';

const mapApiToStore = (item: PresentationFromApi): Presentation => ({
  id: item.ID,
  displayName: item.DisplayName,
  folderId: item.FolderID,
  ownerId: item.OwnerID,
  createdAt: item.CreatedAt,
  updatedAt: item.UpdatedAt,
  deletedAt: item.DeletedAt,
  trashedAt: item.TrashedAt,
  themeId: item.ThemeID,
  originalFolderId: item.OriginalFolderID,
  slides: (item.Slides?.map((item) =>
    mapSlideToFolderManagerItem({
      item: item,
      actions: [
        {
          name: FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION,
          type: FolderManagerItemActionTypes.SINGLE,
        },
      ],
    }),
  ) || []) as FolderManagerItem<PresentationSlideFromApi>[],
});

const mapBuildJobApiToStore = (item: PresentationBuildJobFromApi): PresentationBuildJob => ({
  id: item.ID,
  presentationId: item.presentation_id,
  status: item.status,
  details: item.details,
  outputBucket: item.output_bucket,
  outputKey: item.output_key,
  createdAt: item.created_at,
  updatedAt: item.updated_at,
});

export const mapPresentationToFolderManager = (item: PresentationFromApi): FolderManagerItem => {
  return {
    id: item.ID,
    name: item.DisplayName,
    displayName: item.DisplayName,
    type: FolderManagerItemTypes.PRESENTATION,
    parentId: item.FolderID,
    createdAt: item.CreatedAt,
    updatedAt: item.UpdatedAt,
    deletedAt: item.DeletedAt || null,
    trashedAt: item.TrashedAt || null,
    themeId: item.ThemeID,
    actions: getActionsForItemType(FolderManagerItemTypes.PRESENTATION),
    slides: (item.Slides?.map((item) =>
      mapSlideToFolderManagerItem({
        item: item,
        actions: [
          {
            name: FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION,
            type: FolderManagerItemActionTypes.SINGLE,
          },
        ],
      }),
    ) || []) as FolderManagerItem<PresentationSlideFromApi>[],
  } as FolderManagerItem;
};

export const presentationsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createPresentation: builder.mutation<Presentation, CreatePresentationPayload>({
      query: (data) => ({
        url: `/presentations`,
        method: 'POST',
        body: {
          display_name: data.displayName,
          folder_id: +data.folderId,
        },
      }),
      transformResponse: (result: INgApiResponse<PresentationFromApi>) => mapApiToStore(result.data),
      invalidatesTags: (_result, _error, { folderId }) => [
        { type: 'Folders', id: `PRESENTATIONS_${folderId}` },
        { type: 'Presentations', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              type: 'success',
              message: `Presentation "${arg.displayName}" created successfully.`,
            }),
          );
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Failed to create presentation!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    updatePresentation: builder.mutation<
      Presentation,
      { presentationId: number; data: UpdatePresentationPayload; optimisticSlides?: any[]; currentFolderId?: number }
    >({
      query: ({ presentationId, data }) => ({
        url: `/presentations/${presentationId}`,
        method: 'PATCH',
        body: {
          ...(data.display_name && { display_name: data.display_name }),
          ...(data.folder_id && { folder_id: data.folder_id }),
          ...(data.move_to_trash && { move_to_trash: data.move_to_trash }),
          ...(data.restore_from_trash && { restore_from_trash: data.restore_from_trash }),
          ...(data.theme_id !== undefined && { theme_id: data.theme_id }),
          ...(data.slides !== undefined && { slides: data.slides }),
        },
      }),
      transformResponse: (result: INgApiResponse<PresentationFromApi>) => mapApiToStore(result.data),
      invalidatesTags: () => [{ type: 'Folders' }, { type: 'Presentations' }],
      async onQueryStarted({ presentationId, data, optimisticSlides }, { dispatch, queryFulfilled }) {
        // Optimistic update for slides
        let patchResult: any;
        if (optimisticSlides !== undefined) {
          patchResult = dispatch(
            presentationsService.util.updateQueryData('getPresentation', { presentationId }, (draft) => {
              draft.slides = optimisticSlides;
            }),
          );
        }

        try {
          await queryFulfilled;

          // if (!data.move_to_trash) {
          //   dispatch(
          //     addNotification({
          //       type: 'success',
          //       message: 'Presentation updated successfully.',
          //     }),
          //   );
          // }
        } catch (error: any) {
          // Rollback optimistic update on error
          patchResult?.undo();

          if (isAbortError(error)) return;
          const message = 'Failed to update presentation!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getPresentation: builder.query<Presentation, { presentationId: number }>({
      query: ({ presentationId }) => `/presentations/${presentationId}`,
      transformResponse: (result: INgApiResponse<PresentationFromApi>) => mapApiToStore(result.data),
      providesTags: (_result, _error, { presentationId }) => [{ type: 'Presentations', id: `GET_${presentationId}` }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch presentation!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getPresentationShareLink: builder.query<string, { presentationId: number }>({
      query: ({ presentationId }) => `/presentations/${presentationId}/share`,
      transformResponse: (result: INgApiResponse<string>) => result.data,
      providesTags: (_result, _error, { presentationId }) => [{ type: 'Presentations', id: `SHARE_${presentationId}` }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch presentation share link!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getPresentationBuild: builder.mutation<Blob, { presentationId: number }>({
      queryFn: async ({ presentationId }, _queryApi, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            url: `/presentations/${presentationId}`,
            method: 'GET',
            headers: {
              Accept: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            },
            responseHandler: (response) => response.blob(),
          });

          if (result.error) {
            return { error: result.error };
          }

          return { data: result.data as Blob };
        } catch (error: any) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: isAbortError(error) ? 'Request aborted' : error.message || 'Failed to download presentation',
            },
          };
        }
      },
    }),
    getFolderPresentations: builder.query<FolderManagerItem[], { folderId: number }>({
      query: ({ folderId }) => `/folders/${folderId}/presentations`,
      transformResponse: (result: IApiV2Response<PresentationFromApi>) => {
        const presentations = Array.isArray(result.data) ? result.data : [];
        return presentations.map(mapPresentationToFolderManager);
      },
      providesTags: (_result, _error, { folderId }) => [{ type: 'Folders', id: `PRESENTATIONS_${folderId}` }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch folder presentations!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getTrashPresentations: builder.query<FolderManagerItem[], void>({
      query: () => `/presentations/me/trash`,
      transformResponse: (result: IApiV2Response<PresentationFromApi>) => {
        const presentations = Array.isArray(result.data) ? result.data : [];
        return presentations.map(mapPresentationToFolderManager);
      },
      providesTags: () => [
        { type: 'Folders', id: 'GET_REMOVED_PRESENTATIONS' },
        { type: 'Presentations', id: 'TRASH_LIST' },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch trash presentations!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getLatestPresentationBuild: builder.query<PresentationBuildJob | null, { presentationId: number }>({
      query: ({ presentationId }) => `/presentations/${presentationId}/builds/latest`,
      transformResponse: (result: INgApiResponse<PresentationBuildJobFromApi | null>) => {
        if (!result.data) return null;
        return mapBuildJobApiToStore(result.data);
      },
      providesTags: (_result, _error, { presentationId }) => [{ type: 'Presentations', id: `BUILD_${presentationId}` }],
    }),
    downloadLatestPresentationBuild: builder.query<string, { presentationId: number }>({
      query: ({ presentationId }) => `/presentations/${presentationId}/builds/latest/download`,
      transformResponse: (result: INgApiResponse<string>) => result.data,
    }),
    deletePresentation: builder.mutation<void, { presentationId: number }>({
      query: ({ presentationId }) => ({
        url: `/presentations/${presentationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [
        { type: 'Presentations', id: 'LIST' },
        { type: 'Presentations', id: 'TRASH_LIST' },
        { type: 'Folders' },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              type: 'success',
              message: 'Presentation deleted successfully.',
            }),
          );
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = 'Failed to delete presentation!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
  }),
});

export const {
  useCreatePresentationMutation,
  useUpdatePresentationMutation,
  useGetPresentationQuery,
  useLazyGetPresentationQuery,
  useGetFolderPresentationsQuery,
  useGetTrashPresentationsQuery,
  useGetPresentationShareLinkQuery,
  useDeletePresentationMutation,
  useGetLatestPresentationBuildQuery,
  useGetPresentationBuildMutation,
} = presentationsService;
